import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

// ============================================================================
// Types
// ============================================================================

export type AlertSeverity = 'medium' | 'high' | 'critical'
export type AlertStatus = 'active' | 'acted_on' | 'dismissed'
export type AlertActionType = 'evolution_created' | 'session_scheduled' | 'dismissed'
export type DismissReason = 'already_aware' | 'part_of_condition' | 'already_resolved'

export interface AlertFilters {
  severity?: AlertSeverity | 'all'
  status?: AlertStatus | 'all'
  patientId?: string
}

export interface ClinicalAlert {
  id: string
  patient_id: string
  source_report_id: string | null
  weekly_summary_id: string | null
  insight_text: string
  category: string
  severity: string
  ai_confidence: number | null
  status: AlertStatus
  action_type: AlertActionType | null
  dismiss_reason: DismissReason | null
  action_note: string | null
  acted_by: string | null
  acted_at: string | null
  created_at: string
  patient: {
    id: string
    full_name: string
  }
  source_report?: {
    id: string
    content: string
    report_type: string
    created_at: string
    reporter: {
      relationship: string
      profiles: {
        full_name: string
      }
    } | null
  } | null
  acted_by_profile?: {
    full_name: string
  } | null
}

// ============================================================================
// Fetch alerts
// ============================================================================

async function fetchAlerts(
  organizationId: string,
  userRole: string | undefined,
  profileId: string | undefined,
  filters: AlertFilters,
): Promise<ClinicalAlert[]> {
  let query = supabase
    .from('key_insights')
    .select(`
      id,
      patient_id,
      source_report_id,
      weekly_summary_id,
      insight_text,
      category,
      severity,
      ai_confidence,
      status,
      action_type,
      dismiss_reason,
      action_note,
      acted_by,
      acted_at,
      created_at,
      patients!inner (
        id,
        full_name,
        organization_id
      ),
      source_report:daily_reports (
        id,
        content,
        report_type,
        created_at,
        reporter:family_members (
          relationship,
          profiles (
            full_name
          )
        )
      ),
      acted_by_profile:profiles!key_insights_acted_by_fkey (
        full_name
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .limit(50)

  // Status filter
  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status)
  }

  // Severity filter
  if (filters.severity && filters.severity !== 'all') {
    query = query.eq('severity', filters.severity)
  }

  // Patient filter
  if (filters.patientId) {
    query = query.eq('patient_id', filters.patientId)
  }

  // For professionals (not admins): filter by assigned patients only
  if (userRole === 'professional' && profileId) {
    const { data: assignments } = await supabase
      .from('professional_assignments')
      .select('patient_id')
      .eq('professional_id', profileId)
      .eq('is_active', true)

    const assignedPatientIds = (assignments ?? []).map((a) => a.patient_id)
    if (assignedPatientIds.length > 0) {
      query = query.in('patient_id', assignedPatientIds)
    } else {
      return [] // No assigned patients
    }
  }

  // Order: severity DESC (critical > high > medium), then created_at DESC
  query = query.order('created_at', { ascending: false })

  const { data, error } = await query

  if (error) throw error

  // Map response and sort by severity priority
  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2 }
  const alerts = (data ?? []).map((row: any) => {
    const patient = row.patients as { id: string; full_name: string }
    return {
      ...row,
      patient: { id: patient.id, full_name: patient.full_name },
      source_report: row.source_report || null,
      acted_by_profile: row.acted_by_profile || null,
    } as ClinicalAlert
  })

  alerts.sort((a, b) => {
    const sa = severityOrder[a.severity] ?? 3
    const sb = severityOrder[b.severity] ?? 3
    if (sa !== sb) return sa - sb
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })

  return alerts
}

// ============================================================================
// Hooks
// ============================================================================

export function useAlerts(filters: AlertFilters = { status: 'active' }) {
  const { currentOrganization, userProfile } = useAuth()
  const organizationId = currentOrganization?.id
  const userRole = currentOrganization?.userRole
  const profileId = userProfile?.id

  return useQuery({
    queryKey: ['alerts', organizationId, userRole, profileId, filters],
    queryFn: () => fetchAlerts(organizationId!, userRole, profileId, filters),
    enabled: !!organizationId && userRole !== 'assistant',
  })
}

export function useAlertDetail(insightId: string) {
  return useQuery({
    queryKey: ['alert-detail', insightId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('key_insights')
        .select(`
          *,
          patients (
            id,
            full_name
          ),
          weekly_summaries (
            id,
            summary_text,
            week_start_date,
            week_end_date
          ),
          source_report:daily_reports (
            id,
            content,
            report_type,
            created_at,
            reporter:family_members (
              relationship,
              profiles (
                full_name
              )
            )
          ),
          acted_by_profile:profiles!key_insights_acted_by_fkey (
            full_name
          )
        `)
        .eq('id', insightId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!insightId,
  })
}

export function useAlertBadgeCount() {
  const { currentOrganization, userProfile } = useAuth()
  const organizationId = currentOrganization?.id
  const userRole = currentOrganization?.userRole
  const profileId = userProfile?.id

  return useQuery({
    queryKey: ['alert-badge-count', organizationId, userRole, profileId],
    queryFn: async () => {
      if (userRole === 'assistant') return 0

      if (userRole === 'professional' && profileId) {
        // Get assigned patient IDs first
        const { data: assignments } = await supabase
          .from('professional_assignments')
          .select('patient_id')
          .eq('professional_id', profileId)
          .eq('is_active', true)

        const assignedIds = (assignments ?? []).map((a) => a.patient_id)
        if (assignedIds.length === 0) return 0

        const { count, error } = await supabase
          .from('key_insights')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active')
          .in('patient_id', assignedIds)

        if (error) throw error
        return count ?? 0
      }

      // Admin: count all active alerts in the org (exclude deleted patients)
      const { data: orgPatients } = await supabase
        .from('patients')
        .select('id')
        .eq('organization_id', organizationId!)
        .neq('status', 'deleted')

      const patientIds = (orgPatients ?? []).map((p) => p.id)
      if (patientIds.length === 0) return 0

      const { count, error } = await supabase
        .from('key_insights')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')
        .in('patient_id', patientIds)

      if (error) throw error
      return count ?? 0
    },
    enabled: !!organizationId && userRole !== 'assistant',
    refetchInterval: 30000, // Poll every 30 seconds
  })
}

export function useActOnAlert() {
  const queryClient = useQueryClient()
  const { userProfile } = useAuth()

  return useMutation({
    mutationFn: async (params: {
      insightId: string
      actionType: AlertActionType
      dismissReason?: DismissReason
      note?: string
    }) => {
      const { error } = await supabase
        .from('key_insights')
        .update({
          status: params.actionType === 'dismissed' ? 'dismissed' : 'acted_on',
          action_type: params.actionType,
          dismiss_reason: params.dismissReason || null,
          action_note: params.note || null,
          acted_by: userProfile?.id,
          acted_at: new Date().toISOString(),
        })
        .eq('id', params.insightId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alert-detail'] })
      queryClient.invalidateQueries({ queryKey: ['alert-badge-count'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      queryClient.invalidateQueries({ queryKey: ['patient-insights'] })
    },
  })
}

// Legacy re-exports for backward compatibility during migration
export const useInsights = useAlerts
export const useAcknowledgeInsight = useActOnAlert
export const useInsightDetail = useAlertDetail
export type InsightsFilters = AlertFilters
